import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard, Smartphone, Building2, Wallet, CheckCircle, XCircle, Lock } from 'lucide-react';
import './PaymentPage.css';
import SecondaryFooter from '../Common/SecondaryFooter';

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const paymentData = location.state;

  const [selectedMethod, setSelectedMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);

  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });

  const [upiId, setUpiId] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Redirect if no payment data
    if (!paymentData || !paymentData.productName || !paymentData.billingDetails) {
      navigate('/');
    }
  }, [paymentData, navigate]);

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\s/g, '');
    const formatted = cleaned.match(/.{1,4}/g);
    return formatted ? formatted.join(' ') : cleaned;
  };

  const formatExpiryDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'cardNumber') {
      formattedValue = formatCardNumber(value.replace(/\s/g, '').slice(0, 16));
    } else if (name === 'expiryDate') {
      formattedValue = formatExpiryDate(value);
    } else if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 3);
    }

    setCardDetails(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validatePayment = () => {
    const newErrors = {};

    if (selectedMethod === 'card') {
      if (!cardDetails.cardNumber || cardDetails.cardNumber.replace(/\s/g, '').length !== 16) {
        newErrors.cardNumber = 'Invalid card number';
      }
      if (!cardDetails.cardName.trim()) {
        newErrors.cardName = 'Cardholder name is required';
      }
      if (!cardDetails.expiryDate || !/^\d{2}\/\d{2}$/.test(cardDetails.expiryDate)) {
        newErrors.expiryDate = 'Invalid expiry date';
      }
      if (!cardDetails.cvv || cardDetails.cvv.length !== 3) {
        newErrors.cvv = 'Invalid CVV';
      }
    } else if (selectedMethod === 'upi') {
      if (!upiId.trim() || !/@/.test(upiId)) {
        newErrors.upiId = 'Invalid UPI ID';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!validatePayment()) {
      return;
    }

    setIsProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);

      // Randomly simulate success or failure for demo
      const isSuccess = Math.random() > 0.3; // 70% success rate

      if (isSuccess) {
        alert('✅ Payment Successful!\n\nYour order has been confirmed.\nOrder ID: #' + Math.random().toString(36).substr(2, 9).toUpperCase());
        navigate('/');
      } else {
        alert('❌ Payment Failed!\n\nPlease check your payment details and try again.');
      }
    }, 2500);
  };

  if (!paymentData) {
    return null;
  }

  const { productName, selectedPlan, billingDetails } = paymentData;
  const price = selectedPlan.price || 0;
  const gst = price * 0.18;
  const total = price + gst;

  const paymentMethods = [
    { id: 'card', name: 'Credit/Debit Card', icon: CreditCard },
    { id: 'upi', name: 'UPI', icon: Smartphone },
    { id: 'netbanking', name: 'Net Banking', icon: Building2 },
    { id: 'wallet', name: 'Wallet', icon: Wallet }
  ];

  return (
    <div className="payment-page">
      <div className="payment-header">
        <div className="container">
          <button onClick={() => navigate(-1)} className="back-btn">
            <ArrowLeft size={20} />
            <span>Back to Billing</span>
          </button>
          <h1>Complete Payment</h1>
        </div>
      </div>

      <div className="payment-container">
        <div className="payment-content">
          {/* Left Side - Payment Methods */}
          <div className="payment-section">
            <div className="section-card">
              <div className="secure-badge">
                <Lock size={16} />
                <span>Secure Payment</span>
              </div>

              <h2 className="section-title">Select Payment Method</h2>

              <div className="payment-methods">
                {paymentMethods.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      type="button"
                      className={`method-btn ${selectedMethod === method.id ? 'active' : ''}`}
                      onClick={() => setSelectedMethod(method.id)}
                    >
                      <Icon size={20} />
                      <span>{method.name}</span>
                      {selectedMethod === method.id && (
                        <CheckCircle size={18} className="check-icon" />
                      )}
                    </button>
                  );
                })}
              </div>

              <form onSubmit={handlePayment} className="payment-form">
                {selectedMethod === 'card' && (
                  <div className="card-form">
                    <div className="form-group">
                      <label htmlFor="cardNumber">
                        Card Number <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="cardNumber"
                        name="cardNumber"
                        value={cardDetails.cardNumber}
                        onChange={handleCardChange}
                        className={errors.cardNumber ? 'error' : ''}
                        placeholder="1234 5678 9012 3456"
                      />
                      {errors.cardNumber && <span className="error-msg">{errors.cardNumber}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="cardName">
                        Cardholder Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="cardName"
                        name="cardName"
                        value={cardDetails.cardName}
                        onChange={handleCardChange}
                        className={errors.cardName ? 'error' : ''}
                        placeholder="Name on card"
                      />
                      {errors.cardName && <span className="error-msg">{errors.cardName}</span>}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="expiryDate">
                          Expiry Date <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="expiryDate"
                          name="expiryDate"
                          value={cardDetails.expiryDate}
                          onChange={handleCardChange}
                          className={errors.expiryDate ? 'error' : ''}
                          placeholder="MM/YY"
                        />
                        {errors.expiryDate && <span className="error-msg">{errors.expiryDate}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="cvv">
                          CVV <span className="required">*</span>
                        </label>
                        <input
                          type="password"
                          id="cvv"
                          name="cvv"
                          value={cardDetails.cvv}
                          onChange={handleCardChange}
                          className={errors.cvv ? 'error' : ''}
                          placeholder="123"
                        />
                        {errors.cvv && <span className="error-msg">{errors.cvv}</span>}
                      </div>
                    </div>
                  </div>
                )}

                {selectedMethod === 'upi' && (
                  <div className="upi-form">
                    <div className="form-group">
                      <label htmlFor="upiId">
                        UPI ID <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="upiId"
                        value={upiId}
                        onChange={(e) => {
                          setUpiId(e.target.value);
                          if (errors.upiId) setErrors(prev => ({ ...prev, upiId: '' }));
                        }}
                        className={errors.upiId ? 'error' : ''}
                        placeholder="yourname@upi"
                      />
                      {errors.upiId && <span className="error-msg">{errors.upiId}</span>}
                    </div>

                    <div className="upi-apps">
                      <p className="upi-apps-title">Or pay with</p>
                      <div className="upi-buttons">
                        <button type="button" className="upi-app-btn">Google Pay</button>
                        <button type="button" className="upi-app-btn">PhonePe</button>
                        <button type="button" className="upi-app-btn">Paytm</button>
                      </div>
                    </div>
                  </div>
                )}

                {selectedMethod === 'netbanking' && (
                  <div className="netbanking-form">
                    <div className="form-group">
                      <label htmlFor="bank">
                        Select Bank <span className="required">*</span>
                      </label>
                      <select id="bank" className="bank-select">
                        <option value="">Choose your bank</option>
                        <option value="sbi">State Bank of India</option>
                        <option value="hdfc">HDFC Bank</option>
                        <option value="icici">ICICI Bank</option>
                        <option value="axis">Axis Bank</option>
                        <option value="kotak">Kotak Mahindra Bank</option>
                      </select>
                    </div>
                  </div>
                )}

                {selectedMethod === 'wallet' && (
                  <div className="wallet-form">
                    <div className="wallet-options">
                      <button type="button" className="wallet-btn">
                        <span>Paytm Wallet</span>
                      </button>
                      <button type="button" className="wallet-btn">
                        <span>Amazon Pay</span>
                      </button>
                      <button type="button" className="wallet-btn">
                        <span>PhonePe Wallet</span>
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="pay-btn"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <div className="spinner"></div>
                      <span>Processing Payment...</span>
                    </>
                  ) : (
                    <>
                      <Lock size={20} />
                      <span>Pay ₹{total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </>
                  )}
                </button>

                <p className="payment-notice">
                  By proceeding, you agree to our Terms & Conditions
                </p>
              </form>
            </div>
          </div>

          {/* Right Side - Order Summary */}
          <div className="summary-section">
            <div className="section-card sticky-card">
              <h2 className="section-title">Order Summary</h2>

              <div className="order-details">
                <div className="detail-row">
                  <span className="label">Product</span>
                  <span className="value">{productName}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Plan</span>
                  <span className="value">{selectedPlan.planName}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Duration</span>
                  <span className="value">{selectedPlan.duration === 'M' ? 'Monthly' : 'Yearly'}</span>
                </div>
              </div>

              <div className="divider"></div>

              <div className="billing-info">
                <h3>Billing Address</h3>
                <p>{billingDetails.firstName} {billingDetails.lastName}</p>
                {billingDetails.company && <p>{billingDetails.company}</p>}
                <p>{billingDetails.address}</p>
                <p>{billingDetails.city}, {billingDetails.state} {billingDetails.pincode}</p>
                <p>{billingDetails.country}</p>
                <p className="contact">{billingDetails.email}</p>
                <p className="contact">{billingDetails.phone}</p>
              </div>

              <div className="divider"></div>

              <div className="price-breakdown">
                <div className="price-row">
                  <span>Subtotal</span>
                  <span>₹{price.toLocaleString('en-IN')}</span>
                </div>
                <div className="price-row">
                  <span>GST (18%)</span>
                  <span>₹{gst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="divider"></div>
                <div className="price-row total-row">
                  <span>Total</span>
                  <span className="total-price">₹{total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SecondaryFooter />
    </div>
  );
};

export default PaymentPage;